import { useState } from 'react';
import { motion } from 'framer-motion';
import { Utensils, Sofa as SofaIcon, Bed, Briefcase } from 'lucide-react';

const RoomBasedBrowsing = () => {
  const [activeRoom, setActiveRoom] = useState('living');

  const rooms = [
    {
      id: 'living',
      name: 'Living Room',
      icon: SofaIcon,
      products: [
        { name: 'Contemporary Sofas', image: 'https://images.unsplash.com/photo-1757262798623-a215e869d708?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'TV Units', image: 'https://images.unsplash.com/photo-1760072513442-9872656c1b07?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Coffee Tables', image: 'https://images.unsplash.com/photo-1628745750116-d4e990e4481a?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Accent Chairs', image: 'https://images.pexels.com/photos/30441428/pexels-photo-30441428.jpeg?auto=compress&cs=tinysrgb&w=800' }
      ]
    },
    {
      id: 'dining',
      name: 'Dining Room',
      icon: Utensils,
      products: [
        { name: 'Dining Tables', image: 'https://images.unsplash.com/photo-1751591148854-49c2835dfaa1?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Dining Chairs', image: 'https://images.pexels.com/photos/2089696/pexels-photo-2089696.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { name: 'Sideboards', image: 'https://images.unsplash.com/photo-1708358977332-84e95c0083a6?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Bar Units', image: 'https://images.pexels.com/photos/3926542/pexels-photo-3926542.jpeg?auto=compress&cs=tinysrgb&w=800' }
      ]
    },
    {
      id: 'bedroom',
      name: 'Bedroom',
      icon: Bed,
      products: [
        { name: 'Beds', image: 'https://images.unsplash.com/photo-1765279333918-949ddcb655ba?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Wardrobes', image: 'https://images.unsplash.com/photo-1765434670070-c0ec576eebf3?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Nightstands', image: 'https://images.pexels.com/photos/90319/pexels-photo-90319.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { name: 'Dressers', image: 'https://images.pexels.com/photos/7587468/pexels-photo-7587468.jpeg?auto=compress&cs=tinysrgb&w=800' }
      ]
    },
    {
      id: 'office',
      name: 'Office',
      icon: Briefcase,
      products: [
        { name: 'Office Desks', image: 'https://images.unsplash.com/photo-1758630737900-a28682c5aa69?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Office Chairs', image: 'https://images.unsplash.com/photo-1758691736933-bb0f88fe2e0c?crop=entropy&cs=srgb&fm=jpg&q=85' },
        { name: 'Storage Units', image: 'https://images.pexels.com/photos/20988575/pexels-photo-20988575.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { name: 'Conference Tables', image: 'https://images.pexels.com/photos/5511098/pexels-photo-5511098.jpeg?auto=compress&cs=tinysrgb&w=800' }
      ]
    }
  ];

  const activeRoomData = rooms.find(r => r.id === activeRoom);

  return (
    <section className="py-24 md:py-32 bg-slate-50" data-testid="room-based-browsing">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Shop by Room</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
            Find Furniture for Every Space
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Browse our curated collections organized by room to simplify your furniture discovery.
          </p>
        </div>

        {/* Room Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {rooms.map((room) => (
            <button
              key={room.id}
              onClick={() => setActiveRoom(room.id)}
              data-testid={`room-selector-${room.id}`}
              className={`flex items-center gap-3 px-8 py-4 transition-all duration-300 ${
                activeRoom === room.id
                  ? 'bg-slate-900 text-white shadow-lg scale-105'
                  : 'bg-white text-slate-900 border border-slate-200 hover:border-slate-900 hover:shadow-md'
              }`}
            >
              <room.icon className="w-5 h-5" />
              <span className="text-sm font-bold uppercase tracking-widest">{room.name}</span>
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <motion.div
          key={activeRoom}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6"
          data-testid="room-products-grid"
        >
          {activeRoomData?.products.map((product, index) => (
            <motion.div
              key={`${activeRoom}-${index}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer"
              data-testid={`room-product-${index}`}
            >
              <div className="bg-white border border-slate-200 overflow-hidden transition-all duration-500 hover:shadow-xl hover:border-slate-300">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="font-medium text-lg group-hover:text-bronze-500 transition-colors">{product.name}</h3>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default RoomBasedBrowsing;
